import { getAssetUrl, getImagePropsFromAsset } from '../../index';

import type { Locale } from '@tuic/i18n';
import type { ResponsiveVideoProps } from '@tuic/web-compositions/shared/Video';
import type { Asset } from 'contentful';

type VideoFields = {
  poster?: Asset<'WITHOUT_UNRESOLVABLE_LINKS', Locale>;

  link?: string;
  linkMd?: string;
  linkLg?: string;

  file?: Asset<'WITHOUT_UNRESOLVABLE_LINKS', Locale>;
  fileMd?: Asset<'WITHOUT_UNRESOLVABLE_LINKS', Locale>;
  fileLg?: Asset<'WITHOUT_UNRESOLVABLE_LINKS', Locale>;

  autoPlay?: boolean;
  loop?: boolean;
  controlsHidden?: boolean;
  [key: string]: any;
};

type KindOfVideoEntry = {
  fields?: VideoFields;
  [key: string]: any;
};

export function getVideoPropsFromVideoField(
  entry: undefined,
  props?: Partial<ResponsiveVideoProps>,
): undefined;

export function getVideoPropsFromVideoField(
  entry: KindOfVideoEntry,
  props?: Partial<ResponsiveVideoProps>,
): ResponsiveVideoProps;

export function getVideoPropsFromVideoField(
  entry: KindOfVideoEntry | undefined,
  props?: Partial<ResponsiveVideoProps>,
): ResponsiveVideoProps | undefined;

/**
 * Returns the Video component's props from given Contentful video entry.
 *
 * ```tsx
 * let video = fetchVideoFromContentful();
 *
 * let props = getVideoPropsFromVideoField(video);
 * let videoNode = <ResponsiveVideo {...props} />;
 *
 * let propsWithAdditionalProps = getVideoPropsFromVideoField(video, {
 *   i18n: {
 *     consentMessage: '...',
 *     consentButtonLabel: '...',
 *   }
 * });
 * let videoNodeWithI18n = <ResponsiveVideo {...propsWithAdditionalProps} />;
 * ```
 */
export function getVideoPropsFromVideoField(
  entry: KindOfVideoEntry | undefined,
  props?: Partial<ResponsiveVideoProps>,
): ResponsiveVideoProps | undefined {
  const fields = entry?.fields;

  if (typeof fields === 'undefined') {
    return undefined;
  }

  const poster = fields.poster
    ? getImagePropsFromAsset(fields.poster, {}, 16 / 9)
    : undefined;

  const base: ResponsiveVideoProps = {
    poster,
    controls: fields.controlsHidden !== true,
    loop: fields.loop === true,
    autoPlay: fields.autoPlay === true,
    ...props,
  };

  return {
    ...base,
    src: [
      fields.link ?? getAssetUrl(fields.file),
      {
        md: fields.linkMd ?? getAssetUrl(fields.fileMd),
        lg: fields.linkLg ?? getAssetUrl(fields.fileLg),
      },
    ],
  };
}
